import { Action, Reducer } from 'redux';
import { delay } from 'redux-saga/effects';
import { fork, call, takeEvery, takeLatest, put, all, select } from 'redux-saga/effects';
import { replace } from 'connected-react-router';

import { ITableConfig } from '../components/common/table';

import Api from '../api';

import { IRootState } from './index';

import { actionCreators as layoutActionCreators } from '../reducers/Layout';
import { selectors as authSelectors } from '../reducers/Auth';

import PagedResultSet from '../models/common/PagedResultSet';
import { ODataOptions } from '../util/ODataBuilder';
import Client from '../models/Client';
import api from '../api';
import IdentifyDto from '../models/IdentityDto';

// Store
export interface IClientState {
    isSubmittingAddClient: boolean;
    isSubmittingEditClient: boolean;
    addClientError: string;
    editClientError: string;
    isLoadingClients: boolean;
    clients: PagedResultSet<Client>;
    client: Client;
    clientsPageTableConfig: ITableConfig;
}

const initialState: IClientState = {
    isSubmittingAddClient: false,
    isSubmittingEditClient: false,
    addClientError: '',
    editClientError: '',
    isLoadingClients: false,
    clients: undefined,
    client: undefined,
    clientsPageTableConfig: {
        rowCount: 15,
        pageNumber: 1,
        idField: 'clientNumber',
        columns: [
            { name: 'Number', mapping: 'clientNumber' },
            { name: 'Name', mapping: 'name' },
        ],
    },
};

// Action Types
export enum actionTypes {
    GET_CLIENTS = '[client] GET_CLIENTS',
    GET_CLIENTS_SUCCESS = '[client] GET_CLIENTS_SUCCESS',
    GET_CLIENT = '[client] GET_CLIENT',
    GET_CLIENT_SUCCESS = '[client] GET_CLIENT_SUCCESS',
    ADD_CLIENT = '[client] ADD_CLIENT',
    ADD_CLIENT_SUCCESS = '[client] ADD_CLIENT_SUCCESS',
    ADD_CLIENT_ERROR = '[client] ADD_CLIENT_ERROR',
    TOGGLE_CLIENT_FAVORITE = '[client] TOGGLE_CLIENT_FAVORITE',
    HANDLE_CLIENT_FAVORITE_SUCCESS = '[client] HANDLE_CLIENT_FAVORITE_SUCCESS',
    EDIT_CLIENT = '[client] EDIT_CLIENT',
    EDIT_CLIENT_SUCCESS = '[client] EDIT_CLIENT_SUCCESS',
    EDIT_CLIENT_ERROR = '[client] EDIT_CLIENT_ERROR',
    INCREMENT_CLIENT_PAGE = '[client] INCREMENT_CLIENT_PAGE',
    DECREMENT_CLIENT_PAGE = '[client] DECREMENT_CLIENT_PAGE',
    INITIALIZE_CLIENT_PAGE = '[client] INITIALIZE_CLIENT_PAGE',
    DELETE_CLIENT = '[client] DELETE_CLIENT',
    DELETE_CLIENT_SUCCESS = '[client] DELETE_CLIENT_SUCCESS',
}

// Action Creator
export const actionCreators = {
    incrementClientPage: () => {
        return { type: actionTypes.INCREMENT_CLIENT_PAGE };
    },
    decrementClientPage: () => {
        return { type: actionTypes.DECREMENT_CLIENT_PAGE };
    },
    initializeClientPage: () => {
        return { type: actionTypes.INITIALIZE_CLIENT_PAGE };
    },
    getClients: (options: ODataOptions, resetPage?: boolean) => {
        return { type: actionTypes.GET_CLIENTS, payload: { options, resetPage } };
    },
    getClientsSuccess: (clients: PagedResultSet<Client>, resetPage?: boolean) => {
        return { type: actionTypes.GET_CLIENTS_SUCCESS, payload: { clients, resetPage } };
    },
    getClient: (clientNumber: number) => {
        return { type: actionTypes.GET_CLIENT, payload: clientNumber };
    },
    getClientSuccess: (client: Client) => {
        return { type: actionTypes.GET_CLIENT_SUCCESS, payload: client };
    },
    addClient: (client: Client) => {
        return { type: actionTypes.ADD_CLIENT, payload: client };
    },
    addClientSuccess: (id: IdentifyDto) => {
        return { type: actionTypes.ADD_CLIENT_SUCCESS, payload: id };
    },
    toggleClientFavorite: (clientNumber: number, isFavorite: boolean) => {
        return { type: actionTypes.TOGGLE_CLIENT_FAVORITE, payload: { clientNumber, isFavorite } };
    },
    handleClientFavoriteSuccess: (clientNumber: number, isFavorite: boolean) => {
        return { type: actionTypes.HANDLE_CLIENT_FAVORITE_SUCCESS, payload: { clientNumber, isFavorite } };
    },
    editClient: (client: Client) => {
        return { type: actionTypes.EDIT_CLIENT, payload: client };
    },
    editClientSuccess: (id: IdentifyDto) => {
        return { type: actionTypes.EDIT_CLIENT_SUCCESS, payload: id };
    },
    deleteClient: (clientNumber: number) => {
        return { type: actionTypes.DELETE_CLIENT, payload: clientNumber };
    },
    deleteClientSuccess: (clientNumber: IdentifyDto) => {
        return { type: actionTypes.DELETE_CLIENT_SUCCESS, payload: clientNumber.id };
    },
};

// Reducer
const reducer: Reducer<IClientState> = (state: IClientState = initialState, action: Action): IClientState => {
    switch (action.type) {
        case actionTypes.GET_CLIENT:
            return {
                ...state,
                isLoadingClients: true,
            };
        case actionTypes.GET_CLIENTS:
            return {
                ...state,
                isLoadingClients: true,
            };
        case actionTypes.GET_CLIENT_SUCCESS:
            const getClientPayload = (action as { type: string; payload: Client }).payload;
            const clientResultSet = new PagedResultSet<Client>();
            clientResultSet.items.push(getClientPayload);
            return {
                ...state,
                client: getClientPayload,
                isLoadingClients: false,
            };
        case actionTypes.GET_CLIENTS_SUCCESS:
            const getClientsPayload = (action as { type: string; payload: { clients: PagedResultSet<Client>; resetPage: boolean } }).payload;

            if (getClientsPayload.resetPage) {
                return {
                    ...state,
                    clients: getClientsPayload.clients,
                    isLoadingClients: false,
                };
            } else if (state.clients === undefined || state.clients.pageNumber < getClientsPayload.clients.pageNumber) {
                const newClients = { ...state.clients };
                newClients.pageNumber = getClientsPayload.clients.pageNumber;
                newClients.pageSize = getClientsPayload.clients.pageSize;
                newClients.totalRecords = getClientsPayload.clients.totalRecords;
                if (!newClients.items) {
                    newClients.items = [];
                }
                newClients.items = newClients.items.concat(getClientsPayload.clients.items);
                return {
                    ...state,
                    clients: newClients,
                    isLoadingClients: false,
                };
            }

            return state;

        case actionTypes.ADD_CLIENT:
            return {
                ...state,
                isSubmittingAddClient: true,
                addClientError: '',
            };
        case actionTypes.ADD_CLIENT_SUCCESS:
            return {
                ...state,
                isSubmittingAddClient: false,
                addClientError: '',
            };
        case actionTypes.ADD_CLIENT_ERROR:
            return {
                ...state,
                isSubmittingAddClient: false,
                addClientError: 'error',
            };
        case actionTypes.HANDLE_CLIENT_FAVORITE_SUCCESS:
            const payload = (action as { type: string; payload: { clientNumber: number, isFavorite: boolean } }).payload;
            let clientsList = [...state.clients.items]
            .map(c => c.clientNumber === payload.clientNumber ? {...c, isFavorite: payload.isFavorite} : c);

            return {
                ...state,
                clients: {
                    ...state.clients,
                    items: clientsList
                }
            };
        case actionTypes.EDIT_CLIENT:
            return {
                ...state,
                isSubmittingEditClient: true,
                editClientError: '',
            };
        case actionTypes.EDIT_CLIENT_SUCCESS:
            return {
                ...state,
                isSubmittingEditClient: false,
                editClientError: '',
            };
        case actionTypes.EDIT_CLIENT_ERROR:
            return {
                ...state,
                isSubmittingEditClient: false,
                editClientError: 'error',
            };
        case actionTypes.DELETE_CLIENT:
            return {
                ...state,
                clients: state.clients,
            };
        case actionTypes.DELETE_CLIENT_SUCCESS:
            const deletedClientPayload = (action as { type: string; payload: number }).payload;
            return {
                ...state,
                clients: {
                    ...state.clients,
                    items: state.clients.items.filter((x) => x.clientNumber !== deletedClientPayload),
                },
            };
        case actionTypes.INCREMENT_CLIENT_PAGE:
            return {
                ...state,
                clientsPageTableConfig: {
                    ...state.clientsPageTableConfig,
                    pageNumber: state.clientsPageTableConfig.pageNumber + 1,
                },
            };
        case actionTypes.DECREMENT_CLIENT_PAGE:
            return {
                ...state,
                clientsPageTableConfig: {
                    ...state.clientsPageTableConfig,
                    pageNumber:
                        state.clientsPageTableConfig.pageNumber > 1
                            ? state.clientsPageTableConfig.pageNumber - 1
                            : state.clientsPageTableConfig.pageNumber,
                },
            };
        case actionTypes.INITIALIZE_CLIENT_PAGE:
            return {
                ...state,
                clientsPageTableConfig: {
                    ...state.clientsPageTableConfig,
                    pageNumber: 1,
                },
            };
        default:
            return state;
    }
};

export default reducer;

export const selectors = {
    isSubmittingAddClient: (state: IRootState): boolean => state.client.isSubmittingAddClient,
    isSubmittingEditClient: (state: IRootState): boolean => state.client.isSubmittingEditClient,
    addClientError: (state: IRootState): string => state.client.addClientError,
    editClientError: (state: IRootState): string => state.client.editClientError,
    isLoadingClients: (state: IRootState): boolean => state.client.isLoadingClients || state.layout.initialLoading,
    clients: (state: IRootState): PagedResultSet<Client> => state.client.clients,
    client: (state: IRootState): Client => state.client.client,
    clientsPageTableConfig: (state: IRootState): ITableConfig => state.client.clientsPageTableConfig,
};

// Side-Effects
const sagas = {
    getClients: function* (action: { type: string; payload: { options: ODataOptions; resetPage: boolean } }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const tableConfig = yield select(selectors.clientsPageTableConfig);
        // Explicit pages support the admin pager without changing the main index's incremental paging.
        const requestedPage = action.payload?.options?.getPageNumber();
        const options = ODataOptions.from({ ...tableConfig, pageNumber: requestedPage || tableConfig.pageNumber });

        if (action.payload) {
            options.merge(action.payload.options);
        }

        const [result] = yield all([call(Api.Client.getAll, options, jwt), delay(200)]);

        yield put(actionCreators.getClientsSuccess(result, action.payload && action.payload.resetPage));
    },
    getClient: function* (action: { type: string; payload: number }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const clientNumber = action.payload;
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(Api.Client.get, clientNumber, jwt), delay(200)]);
        yield put(actionCreators.getClientSuccess(result));
    },
    addClient: function* (action: { type: string; payload: Client }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(api.Client.create, action.payload, jwt), delay(200)]);
        yield put(actionCreators.addClientSuccess(result));
        yield put(replace(`/clients/${result.id}`));
    },
    toggleClientFavorite: function* (action: { type: string; payload: { clientNumber: number, isFavorite: boolean } }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        if (action.payload.isFavorite === true) {
            yield all([call(Api.Client.addFavorite, action.payload.clientNumber, jwt), delay(200)]);
        } else {
            yield all([call(Api.Client.removeFavorite, action.payload.clientNumber, jwt), delay(200)]);
        }
        yield put(actionCreators.handleClientFavoriteSuccess(action.payload.clientNumber, action.payload.isFavorite));
    },
    editClient: function* (action: { type: string; payload: Client }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(api.Client.update, action.payload, jwt), delay(200)]);
        yield put(actionCreators.editClientSuccess(result));
        const [result2] = yield all([call(Api.Client.get, action.payload.clientNumber, jwt)]);
        yield put(actionCreators.getClientSuccess(result2));

        yield put(replace(`/clients/${result.id}`));
    },
    deleteClient: function* (action: { type: string; payload: number }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(Api.Client.delete, action.payload, jwt), delay(200)]);
        yield put(actionCreators.deleteClientSuccess(result));
        yield put(replace(`/admin`));
        yield put(replace(`/admin/clients`));
    },
};

const watches = {
    incrementClick: function* () {
        yield takeEvery(actionTypes.INCREMENT_CLIENT_PAGE, sagas.getClients);
    },
    decrementClick: function* () {
        yield takeEvery(actionTypes.DECREMENT_CLIENT_PAGE, sagas.getClients);
    },
    getClients: function* () {
        yield takeLatest(actionTypes.GET_CLIENTS, sagas.getClients);
    },
    getClient: function* () {
        yield takeEvery(actionTypes.GET_CLIENT, sagas.getClient);
    },
    addClient: function* () {
        yield takeEvery(actionTypes.ADD_CLIENT, sagas.addClient);
    },
    toggleClientFavorite: function* () {
        yield takeEvery(actionTypes.TOGGLE_CLIENT_FAVORITE, sagas.toggleClientFavorite);
    },
    editClient: function* () {
        yield takeEvery(actionTypes.EDIT_CLIENT, sagas.editClient);
    },
    deleteClient: function* () {
        yield takeEvery(actionTypes.DELETE_CLIENT, sagas.deleteClient);
    },
};

export function* saga() {
    yield fork(watches.incrementClick);
    yield fork(watches.decrementClick);
    yield fork(watches.getClients);
    yield fork(watches.getClient);
    yield fork(watches.addClient);
    yield fork(watches.toggleClientFavorite);
    yield fork(watches.editClient);
    yield fork(watches.deleteClient);
}



// WEBPACK FOOTER //
// ./src/reducers/Client.ts